// Complete fixes for the Designer QR Code component to ensure proper functionality

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  FormControlLabel,
  Switch,
  Slider,
  Grid,
  Tooltip,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Upload,
  Wand2,
  Camera,
  Palette,
  PenTool,
  Image as ImageIcon,
  Download,
  Save,
  Share2,
  HelpCircle,
  RefreshCw,
  Edit2,
  Check,
  X
} from 'lucide-react';
import QRCode from 'qrcode';

// Designer QR Code Component
const DesignerQRCode = ({
  qrValue,
  isTrial = false,
  profileData,
  onGenerated,
  navigate,
  size = 300
}) => {
  // Canvas references
  const qrCanvasRef = useRef(null);
  const sourceImageCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);

  // State
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDesignerMode, setIsDesignerMode] = useState(true); // Default to true to show controls
  const [designerImageType, setDesignerImageType] = useState('profile'); // 'profile', 'company', 'custom'
  const [customImage, setCustomImage] = useState(null);
  const [customImageUrl, setCustomImageUrl] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [generatedQrUrl, setGeneratedQrUrl] = useState(''); // Store the generated QR URL

  // QR Design Settings
  const [dotShape, setDotShape] = useState('circle'); // 'square', 'circle', 'rounded'
  const [colorMode, setColorMode] = useState('original'); // 'original', 'mono', 'gradient'
  const [primaryColor, setPrimaryColor] = useState('#000000');
  const [secondaryColor, setSecondaryColor] = useState('#666666');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [contrastLevel, setContrastLevel] = useState(50);
  const [brightnessLevel, setBrightnessLevel] = useState(50);
  const [imageBlendLevel, setImageBlendLevel] = useState(70);

  // Get source image based on selected type
  const getSourceImage = () => {
    if (designerImageType === 'profile') {
      // Ensure we have a valid profile image URL
      return profileData.avatar && profileData.avatar !== '' 
        ? profileData.avatar 
        : 'https://via.placeholder.com/200x200?text=Profile';
    } else if (designerImageType === 'company') {
      // Try to get company logo from domain or company URL
      if (profileData.companyUrl) {
        // Extract domain from company URL
        try {
          const url = new URL(profileData.companyUrl.startsWith('http') 
            ? profileData.companyUrl 
            : `https://${profileData.companyUrl}`);
          
          const domain = url.hostname;
          return `https://logo.clearbit.com/${domain}`;
        } catch (e) {
          console.error("Invalid company URL:", profileData.companyUrl);
        }
      }
      
      // Use company domain if available
      if (profileData.domain) {
        const cleanDomain = profileData.domain.replace(/^https?:\/\//, '').split('/')[0];
        return `https://logo.clearbit.com/${cleanDomain}`;
      }
      
      // Fallback to a default company icon
      return 'https://via.placeholder.com/200x200?text=Company';
    } else {
      // Custom uploaded image
      return customImageUrl || 'https://via.placeholder.com/200x200?text=Upload';
    }
  };

  // Generate vCard data for contact QR
  const generateVCard = () => {
    const { name, title, company, email, phone, companyAddress, companyUrl, linkedinUrl, bio } = profileData;
    
    return `BEGIN:VCARD
VERSION:3.0
FN:${name || ''}
TITLE:${title || ''}
ORG:${company || ''}
EMAIL${email ? ';type=WORK' : ''}:${email || ''}
TEL${phone ? ';type=CELL' : ''}:${phone || ''}
ADR${companyAddress ? ';type=WORK' : ''}:;;${companyAddress || ''}
URL${companyUrl ? ';type=WORK' : ''}:${companyUrl || ''}
URL${linkedinUrl ? ';type=LINKEDIN' : ''}:${linkedinUrl || ''}
NOTE:${bio || ''}
END:VCARD`;
  };

  // Generate standard QR code (fallback method)
  const generateStandardQR = async () => {
    if (!qrCanvasRef.current) return;

    try {
      // Reset canvas dimensions to ensure square aspect ratio
      qrCanvasRef.current.width = size;
      qrCanvasRef.current.height = size;
      
      // Log the QR value to help debug
      console.log("Generating standard QR with value:", qrValue);
      
      // Use the qrcode library with proper options
      await QRCode.toCanvas(qrCanvasRef.current, qrValue || 'https://example.com', {
        errorCorrectionLevel: 'H',
        margin: 4,
        width: size - 8, // Slightly smaller than canvas for better display
        color: {
          dark: primaryColor,
          light: bgColor
        }
      });

      // Create preview and send to parent component
      createPreview();

      if (onGenerated && qrCanvasRef.current) {
        qrCanvasRef.current.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          setGeneratedQrUrl(url); // Store locally
          onGenerated(url); // Pass to parent
        });
      }
    } catch (error) {
      console.error("Error generating standard QR code:", error);
    }
  };

  // Function to generate Designer QR Code
  const generateDesignerQR = async () => {
    if (isTrial) {
      // For trial users, just show the upgrade prompt
      navigate('/profiles/upgrade');
      return;
    }

    setIsGenerating(true);

    try {
      const canvas = qrCanvasRef.current;
      if (!canvas) throw new Error("Canvas not found");

      // Reset canvas dimensions to ensure square aspect ratio
      canvas.width = size;
      canvas.height = size;
      
      const ctx = canvas.getContext('2d');

      // Clear canvas and set background
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Generate a standard QR code to temporary canvas
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = size;
      tempCanvas.height = size;

      // Debugging: Log the QR value
      console.log("Generating designer QR for value:", qrValue);
      
      // Use higher error correction for better reliability with image overlay
      await QRCode.toCanvas(tempCanvas, qrValue || 'https://example.com', {
        errorCorrectionLevel: 'H', // Highest error correction
        margin: 1, // Reduced margin
        width: size - 20, // Slightly smaller for better detection
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Extract QR code data more reliably
      const tempCtx = tempCanvas.getContext('2d');
      const qrImageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const qrData = qrImageData.data;

      // Load source image with proper error handling
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        try {
          // Draw source image to hidden canvas
          const sourceCanvas = sourceImageCanvasRef.current;
          if (!sourceCanvas) throw new Error("Source canvas not found");

          const sourceCtx = sourceCanvas.getContext('2d');
          
          // Important: Resize the source canvas to match QR dimensions
          sourceCanvas.width = size;
          sourceCanvas.height = size;

          // Draw the image while maintaining aspect ratio
          const imgAspect = img.width / img.height;
          let drawWidth = size;
          let drawHeight = size;
          let offsetX = 0;
          let offsetY = 0;
          
          if (imgAspect > 1) {
            // Landscape image
            drawHeight = size / imgAspect;
            offsetY = (size - drawHeight) / 2;
          } else {
            // Portrait image
            drawWidth = size * imgAspect;
            offsetX = (size - drawWidth) / 2;
          }
          
          // Fill with white first (important for transparency)
          sourceCtx.fillStyle = '#FFFFFF';
          sourceCtx.fillRect(0, 0, size, size);
          
          // Draw the image centered
          sourceCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

          // Get image data after proper sizing
          const imageData = sourceCtx.getImageData(0, 0, size, size);
          const pixels = imageData.data;

          // Draw white background on main canvas
          ctx.fillStyle = bgColor;
          ctx.fillRect(0, 0, size, size);

          // Calculate QR code module size more accurately
          // QR codes are always square with an odd number of modules
          const numModules = Math.sqrt(qrData.length / 4);  // Calculate based on data length
          const moduleSize = size / numModules;
          
          console.log("QR dimensions:", {
            moduleCount: numModules,
            moduleSize,
            canvasSize: size
          });

          // Loop through QR code data and draw modules
          for (let y = 0; y < numModules; y++) {
            for (let x = 0; x < numModules; x++) {
              const idx = (Math.floor(y * numModules) + Math.floor(x)) * 4;
              
              // Only draw dark modules (ensure index is valid)
              if (idx >= 0 && idx < qrData.length && qrData[idx] < 128) {
                // Improved finder pattern detection
                const isFinderPattern = 
                  (x < 7 && y < 7) || // top-left
                  (x > numModules - 8 && y < 7) || // top-right
                  (x < 7 && y > numModules - 8);   // bottom-left
                
                if (isFinderPattern) continue; // Skip finder patterns
                
                // Map to image coordinates - now direct mapping since we've sized the source image
                const imgX = Math.floor(x);
                const imgY = Math.floor(y);
                const imgIdx = (imgY * size + imgX) * 4;
                
                // Get pixel color with bounds checking
                let r = 0, g = 0, b = 0;
                if (imgIdx >= 0 && imgIdx < pixels.length - 3) {
                  r = pixels[imgIdx];
                  g = pixels[imgIdx + 1];
                  b = pixels[imgIdx + 2];
                }

                // Apply contrast and brightness
                const contrastFactor = (contrastLevel - 50) / 50;
                const brightnessFactor = (brightnessLevel - 50) / 50;

                r = Math.min(255, Math.max(0, Math.floor((r - 128) * (contrastFactor + 1) + 128 + brightnessFactor * 128)));
                g = Math.min(255, Math.max(0, Math.floor((g - 128) * (contrastFactor + 1) + 128 + brightnessFactor * 128)));
                b = Math.min(255, Math.max(0, Math.floor((b - 128) * (contrastFactor + 1) + 128 + brightnessFactor * 128)));

                // Apply color mode
                let finalColor;
                if (colorMode === 'original') {
                  // Darken colors for better visibility
                  const brightness = (r + g + b) / 3;
                  const darkFactor = Math.min(0.7, brightness / 255);

                  r = Math.floor(r * (1 - darkFactor * 0.8));  // Less darkening for better color reproduction
                  g = Math.floor(g * (1 - darkFactor * 0.8));
                  b = Math.floor(b * (1 - darkFactor * 0.8));

                  finalColor = `rgb(${r}, ${g}, ${b})`;
                } else if (colorMode === 'mono') {
                  const color = hexToRgb(primaryColor);
                  finalColor = `rgb(${color.r}, ${color.g}, ${color.b})`;
                } else if (colorMode === 'gradient') {
                  const brightness = (r + g + b) / 3;
                  const ratio = brightness / 255;

                  const c1 = hexToRgb(primaryColor);
                  const c2 = hexToRgb(secondaryColor);

                  const blendedR = Math.floor(c1.r * (1 - ratio) + c2.r * ratio);
                  const blendedG = Math.floor(c1.g * (1 - ratio) + c2.g * ratio);
                  const blendedB = Math.floor(c1.b * (1 - ratio) + c2.b * ratio);

                  finalColor = `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
                }

                // Draw module with specified shape
                const xPos = x * moduleSize;
                const yPos = y * moduleSize;
                const moduleWidth = moduleSize * 0.9; // Slightly smaller for better separation

                ctx.fillStyle = finalColor;

                if (dotShape === 'square') {
                  ctx.fillRect(xPos, yPos, moduleWidth, moduleWidth);
                } else if (dotShape === 'circle') {
                  ctx.beginPath();
                  ctx.arc(
                    xPos + moduleSize / 2,
                    yPos + moduleSize / 2,
                    moduleWidth / 2,
                    0,
                    Math.PI * 2
                  );
                  ctx.fill();
                } else if (dotShape === 'rounded') {
                  const radius = moduleWidth / 4;
                  ctx.beginPath();
                  ctx.moveTo(xPos + radius, yPos);
                  ctx.lineTo(xPos + moduleWidth - radius, yPos);
                  ctx.quadraticCurveTo(xPos + moduleWidth, yPos, xPos + moduleWidth, yPos + radius);
                  ctx.lineTo(xPos + moduleWidth, yPos + moduleWidth - radius);
                  ctx.quadraticCurveTo(xPos + moduleWidth, yPos + moduleWidth, xPos + moduleWidth - radius, yPos + moduleWidth);
                  ctx.lineTo(xPos + radius, yPos + moduleWidth);
                  ctx.quadraticCurveTo(xPos, yPos + moduleWidth, xPos, yPos + moduleWidth - radius);
                  ctx.lineTo(xPos, yPos + radius);
                  ctx.quadraticCurveTo(xPos, yPos, xPos + radius, yPos);
                  ctx.fill();
                }
              }
            }
          }

          // Fixed finder pattern drawing with correct size calculation
          const drawFinderPattern = (x, y, size) => {
            // Outer square
            ctx.fillStyle = '#000000';
            ctx.fillRect(x, y, size, size);

            // Middle white square
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(x + size / 7, y + size / 7, size * 5 / 7, size * 5 / 7);

            // Inner black square
            ctx.fillStyle = '#000000';
            ctx.fillRect(x + size * 2 / 7, y + size * 2 / 7, size * 3 / 7, size * 3 / 7);
          };

          const finderSize = moduleSize * 7;

          // Draw finder patterns with correct positioning
          drawFinderPattern(0, 0, finderSize);
          drawFinderPattern(size - finderSize, 0, finderSize);
          drawFinderPattern(0, size - finderSize, finderSize);

          // Draw timing patterns
          ctx.fillStyle = '#000000';

          // Horizontal timing pattern
          for (let i = 8; i < numModules - 8; i += 2) {
            ctx.fillRect(i * moduleSize, 6 * moduleSize, moduleSize, moduleSize);
          }

          // Vertical timing pattern
          for (let i = 8; i < numModules - 8; i += 2) {
            ctx.fillRect(6 * moduleSize, i * moduleSize, moduleSize, moduleSize);
          }

          // Draw alignment patterns for larger QR codes
          if (numModules >= 25) { // Version 2 and above
            const alignmentPositions = [numModules - 7];

            for (const posX of alignmentPositions) {
              for (const posY of alignmentPositions) {
                // Skip if overlapping with finder patterns
                if ((posX <= 7 && posY <= 7) ||
                  (posX <= 7 && posY >= numModules - 8) ||
                  (posX >= numModules - 8 && posY <= 7)) {
                  continue;
                }

                const x = posX * moduleSize;
                const y = posY * moduleSize;
                const alignSize = moduleSize * 5;

                // Outer square
                ctx.fillStyle = '#000000';
                ctx.fillRect(x - alignSize / 2, y - alignSize / 2, alignSize, alignSize);

                // Middle white square
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(x - alignSize / 2 + moduleSize, y - alignSize / 2 + moduleSize, alignSize - 2 * moduleSize, alignSize - 2 * moduleSize);

                // Center black dot
                ctx.fillStyle = '#000000';
                ctx.fillRect(x - moduleSize / 2, y - moduleSize / 2, moduleSize, moduleSize);
              }
            }
          }

          // Create preview and callback
          createPreview();

          if (onGenerated) {
            canvas.toBlob((blob) => {
              const url = URL.createObjectURL(blob);
              setGeneratedQrUrl(url); // Store locally
              onGenerated(url); // Pass to parent
            });
          }
        } catch (error) {
          console.error("Error processing image:", error);
          generateStandardQR(); // Fallback to standard QR
        }

        setIsGenerating(false);
      };

      img.onerror = (err) => {
        console.error("Error loading image:", err);
        // Try with a proxy if CORS is the issue
        const proxyUrl = `https://cors-anywhere.herokuapp.com/${getSourceImage()}`;
        console.log("Trying with proxy:", proxyUrl);
        
        // Second attempt with proxy
        img.onerror = () => {
          console.error("Failed to load image even with proxy, falling back to standard QR");
          generateStandardQR();
          setIsGenerating(false);
        };
        
        try {
          img.src = proxyUrl;
        } catch (error) {
          console.error("Error setting proxy URL:", error);
          generateStandardQR();
          setIsGenerating(false);
        }
      };

      try {
        img.src = getSourceImage();
      } catch (error) {
        console.error("Error setting image source:", error);
        generateStandardQR();
        setIsGenerating(false);
      }

    } catch (error) {
      console.error("Error generating designer QR:", error);
      generateStandardQR(); // Fallback to standard QR
      setIsGenerating(false);
    }
  };

  // Helper function to convert hex to RGB
  const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  // Create a small preview of the QR code
  const createPreview = () => {
    const previewCanvas = previewCanvasRef.current;
    const mainCanvas = qrCanvasRef.current;

    if (!previewCanvas || !mainCanvas) return;

    try {
      // Ensure preview canvas is square
      previewCanvas.width = 100;
      previewCanvas.height = 100;
      
      const previewCtx = previewCanvas.getContext('2d');
      previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
      previewCtx.drawImage(mainCanvas, 0, 0, previewCanvas.width, previewCanvas.height);
    } catch (error) {
      console.error("Error creating preview:", error);
    }
  };

  // Download QR code
  const downloadQR = () => {
    if (isTrial) {
      navigate('/profiles/upgrade');
      return;
    }

    const canvas = qrCanvasRef.current;
    if (!canvas) return;

    try {
      const url = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `designer-qr-${profileData?.name?.toLowerCase().replace(/\s+/g, '-') || 'qrcode'}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error("Error downloading QR code:", error);
    }
  };

  // Handle custom image upload
  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomImage(file);
      setCustomImageUrl(e.target?.result?.toString() || '');
      setDesignerImageType('custom');
      setShowUploadDialog(false);
    };
    reader.readAsDataURL(file);
  };

  // Reset QR code to default settings
  const resetSettings = () => {
    setDotShape('circle');
    setColorMode('original');
    setPrimaryColor('#000000');
    setSecondaryColor('#666666');
    setBgColor('#ffffff');
    setContrastLevel(50);
    setBrightnessLevel(50);
    setImageBlendLevel(70);
  };

  // Effect to set canvas size
  useEffect(() => {
    if (qrCanvasRef.current) {
      // Always keep canvas square with exact dimensions
      qrCanvasRef.current.width = size;
      qrCanvasRef.current.height = size;
    }
    if (sourceImageCanvasRef.current) {
      sourceImageCanvasRef.current.width = size;
      sourceImageCanvasRef.current.height = size;
    }
    if (previewCanvasRef.current) {
      previewCanvasRef.current.width = 100;
      previewCanvasRef.current.height = 100;
    }
  }, [size]);

  // Effect to generate QR code when component mounts or when relevant props change
  useEffect(() => {
    if (isDesignerMode && !isTrial) {
      // Don't auto-generate, wait for user to click the button
      // But we can pre-generate a standard QR to show something
      generateStandardQR();
    } else {
      // For trial users or when designer mode is off, generate standard QR code
      generateStandardQR();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesignerMode, isTrial, qrValue, primaryColor, bgColor]);

  return (
    <Box>
      {/* Designer Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Designer QR Code
          <Tooltip title="Create QR codes that incorporate your image or logo into the design">
            <IconButton size="small">
              <HelpCircle size={16} />
            </IconButton>
          </Tooltip>
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={isDesignerMode}
              onChange={(e) => {
                if (isTrial) {
                  navigate('/profiles/upgrade');
                } else {
                  setIsDesignerMode(e.target.checked);
                  if (!e.target.checked) {
                    generateStandardQR();
                  }
                }
              }}
              disabled={isTrial}
            />
          }
          label={
            <Typography variant="body2">
              {isTrial ? "Premium Feature" : "Designer Mode"}
            </Typography>
          }
        />
      </Box>

      {/* Upgrade notice for trial users */}
      {isTrial && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          icon={<Wand2 size={24} />}
        >
          <AlertTitle>Unlock Designer QR Codes</AlertTitle>
          Create stunning QR codes that integrate your brand imagery directly into the QR pattern.
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => navigate('/profiles/upgrade')}
            sx={{ mt: 1 }}
          >
            Upgrade to Access
          </Button>
        </Alert>
      )}

      {/* Designer Controls */}
      {isDesignerMode && !isTrial && (
        <Grid container spacing={2}>
          {/* Image Selection */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>
                Source Image
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Image Source</InputLabel>
                  <Select
                    value={designerImageType}
                    onChange={(e) => setDesignerImageType(e.target.value)}
                    label="Image Source"
                  >
                    <MenuItem value="profile">Profile Photo</MenuItem>
                    <MenuItem value="company">Company Logo</MenuItem>
                    <MenuItem value="custom">Custom Upload</MenuItem>
                  </Select>
                </FormControl>

                <Box
                  sx={{
                    width: '100%',
                    height: 150,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {designerImageType === 'custom' && !customImageUrl ? (
                    <Button
                      startIcon={<Upload />}
                      onClick={() => setShowUploadDialog(true)}
                    >
                      Upload Image
                    </Button>
                  ) : (
                    <>
                      <img
                        src={getSourceImage()}
                        alt="Source"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          // Handle image load error
                          e.target.src = 'https://via.placeholder.com/200x200?text=Image+Error';
                        }}
                      />
                      {designerImageType === 'custom' && (
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            bgcolor: 'rgba(255,255,255,0.7)'
                          }}
                          onClick={() => setShowUploadDialog(true)}
                        >
                          <Edit2 size={16} />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Wand2 />}
                  onClick={generateDesignerQR}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate Designer QR'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Style Controls */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>
                Design Settings
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Dot Shape</InputLabel>
                  <Select
                    value={dotShape}
                    onChange={(e) => setDotShape(e.target.value)}
                    label="Dot Shape"
                  >
                    <MenuItem value="square">Square</MenuItem>
                    <MenuItem value="circle">Circle</MenuItem>
                    <MenuItem value="rounded">Rounded Square</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Color Mode</InputLabel>
                  <Select
                    value={colorMode}
                    onChange={(e) => setColorMode(e.target.value)}
                    label="Color Mode"
                  >
                    <MenuItem value="original">Original Colors</MenuItem>
                    <MenuItem value="mono">Monochrome</MenuItem>
                    <MenuItem value="gradient">Gradient</MenuItem>
                  </Select>
                </FormControl>

                {(colorMode === 'mono' || colorMode === 'gradient') && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" display="block">
                        Primary
                      </Typography>
                      <TextField
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        sx={{ width: 80 }}
                        size="small"
                      />
                    </Box>

                    {colorMode === 'gradient' && (
                      <Box>
                        <Typography variant="caption" display="block">
                          Secondary
                        </Typography>
                        <TextField
                          type="color"
                          value={secondaryColor}
                          onChange={(e) => setSecondaryColor(e.target.value)}
                          sx={{ width: 80 }}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" display="block">
                    Background
                  </Typography>
                  <TextField
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    sx={{ width: 80 }}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" display="block" gutterBottom>
                    Contrast: {contrastLevel}%
                  </Typography>
                  <Slider
                    value={contrastLevel}
                    onChange={(e, newValue) => setContrastLevel(newValue)}
                    min={0}
                    max={100}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" display="block" gutterBottom>
                    Brightness: {brightnessLevel}%
                  </Typography>
                  <Slider
                    value={brightnessLevel}
                    onChange={(e, newValue) => setBrightnessLevel(newValue)}
                    min={0}
                    max={100}
                    size="small"
                  />
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<RefreshCw />}
                  size="small"
                  onClick={resetSettings}
                >
                  Reset Settings
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Preview */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                QR Code Preview
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexGrow: 1,
                  position: 'relative',
                  minHeight: '200px',
                  width: '100%',
                  maxWidth: `${size}px`,
                  margin: '0 auto',
                  aspectRatio: '1 / 1'
                }}
              >
                {isGenerating ? (
                  <CircularProgress size={40} />
                ) : (
                  <canvas
                    ref={qrCanvasRef}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                )}
              </Box>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={downloadQR}
                  disabled={isGenerating}
                >
                  Download
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  onClick={() => {
                    // Use the QR code in the main component
                    if (onGenerated && qrCanvasRef.current) {
                      qrCanvasRef.current.toBlob((blob) => {
                        const url = URL.createObjectURL(blob);
                        setGeneratedQrUrl(url);
                        onGenerated(url);
                      });
                    }
                  }}
                  disabled={isGenerating}
                >
                  Use This QR
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Standard QR Code Display */}
      {(!isDesignerMode || isTrial) && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: 2,
          p: 3,
          border: '1px solid #e0e0e0',
          borderRadius: 2
        }}>
          <Typography variant="subtitle2" gutterBottom>
            QR Code Preview
          </Typography>

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1, 
            position: 'relative',
            width: '100%', 
            maxWidth: `${size}px`, 
            margin: 'auto', 
            aspectRatio: '1 / 1',
          }}>
            {isGenerating ? (
              <CircularProgress size={40} />
            ) : (
              <canvas
                ref={qrCanvasRef}
                style={{
                  maxWidth: '100%', 
                  maxHeight: '100%',
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            )}

            {/* Overlay for trial users */}
            {isTrial && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                backdropFilter: 'blur(2px)'
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/profiles/upgrade')}
                >
                  Upgrade to Access
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadQR}
              disabled={isTrial || isGenerating}
            >
              Download
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={() => {
                if (onGenerated && qrCanvasRef.current) {
                  qrCanvasRef.current.toBlob((blob) => {
                    const url = URL.createObjectURL(blob);
                    setGeneratedQrUrl(url);
                    onGenerated(url);
                  });
                }
              }}
              disabled={isTrial || isGenerating}
            >
              Use This QR
            </Button>
          </Box>
        </Box>
      )}

      {/* Hidden canvases for processing */}
      <canvas
        ref={sourceImageCanvasRef}
        style={{ display: 'none' }}
      />
      <canvas
        ref={previewCanvasRef}
        style={{ display: 'none' }}
      />

      {/* Image Upload Dialog */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Custom Image</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Upload an image to use in your designer QR code. Simple images with clear contrasts work best.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 3,
              minHeight: 200,
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
              }
            }}
            component="label"
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageUpload}
            />
            <Upload size={40} color="#999" />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Click to upload or drag image here
            </Typography>
            <Typography variant="caption" color="text.secondary">
              JPG, PNG or GIF (max. 2MB)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)} startIcon={<X />}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Optional: Add a separate area to display the final QR code outside the generator UI */}
      {generatedQrUrl && (
        <Box sx={{ 
          mt: 4, 
          textAlign: 'center',
          display: 'none' // Set to 'block' if you want to show this section
        }}>
          <Typography variant="h6" gutterBottom>
            Your Artistic QR Code:
          </Typography>
          <Box
            sx={{
              width: '300px',
              height: '300px',
              margin: '0 auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img 
              src={generatedQrUrl} 
              alt="Generated QR Code" 
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'block'
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DesignerQRCode;